/**
 * Raíz de rutas de Staff (T019): provider de TanStack Query, `<Outlet/>` y el puente entre el
 * cliente API y el router — `api/client.ts` (T018) emite los eventos de `api/events.ts` y aquí se
 * traducen en navegación a las pantallas de acceso (implementation-notes 002 §4):
 *
 * - `unauthenticated` / `deviceMismatch` → `/dispositivo-no-registrado` (US3)
 * - `workerInactive` → `/cuenta-inactiva` (US5)
 * - `companySuspended` → `/servicio-suspendido` (US5)
 * - `privacyRequired` → enciende el flag de `features/access/privacy-store.ts` (lo consume US2)
 *
 * `errorComponent` (006 · T056, research R-24; contracts/staff-ui.md §6): captura cualquier error
 * no previsto que escape de una pantalla (una excepción de render, una respuesta que no valida
 * contra su esquema) y pinta `GenericErrorScreen` — nunca el código, el `request_id` ni una traza
 * (la observabilidad de ese 5xx ya quedó en los logs de la API con su `request_id`). "Reintentar"
 * invalida las consultas de React Query y recarga la ruta actual (`router.invalidate()`) antes de
 * salir del límite de error (`reset()`), para que la pantalla vuelva a pedir sus datos. El
 * `CatchBoundary` de la ruta raíz envuelve a `RootComponent` —y por lo tanto a su
 * `QueryClientProvider`— así que, atrapado el error, ese provider deja de estar montado: por eso
 * `RootErrorComponent` usa el singleton `queryClient` de `api/query-client.ts` en vez de
 * `useQueryClient()`.
 *
 * `InstallHost` monta una sola vez la hoja de instalación con la guía animada (features/install).
 */
import { QueryClientProvider } from '@tanstack/react-query';
import {
  createRootRoute,
  Link,
  Outlet,
  useNavigate,
  useRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import { useEffect } from 'react';

import { FERMIN_EVENTS, onFerminEvent } from '../api/events';
import { queryClient } from '../api/query-client';
import { buttonClasses } from '../components/ui/button';
import { GenericErrorScreen } from '../components/ui/generic-error';
import { Screen } from '../components/ui/screen';
import { setPrivacyRequired } from '../features/access/privacy-store';
import { InstallHost } from '../features/install/install-host';
import { t } from '../i18n';

/** Destino de cada evento que implica salir de la pantalla actual. */
export const ACCESS_ROUTES = {
  [FERMIN_EVENTS.unauthenticated]: '/dispositivo-no-registrado',
  [FERMIN_EVENTS.deviceMismatch]: '/dispositivo-no-registrado',
  [FERMIN_EVENTS.workerInactive]: '/cuenta-inactiva',
  [FERMIN_EVENTS.companySuspended]: '/servicio-suspendido',
} as const;

function FerminEventsBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribers = [
      ...Object.entries(ACCESS_ROUTES).map(([eventName, to]) =>
        onFerminEvent(eventName as keyof typeof ACCESS_ROUTES, () => {
          // La sesión ya no sirve: se vacía la caché para no mostrar datos de otro estado.
          queryClient.clear();
          void navigate({ to, replace: true });
        }),
      ),
      onFerminEvent(FERMIN_EVENTS.privacyRequired, () => setPrivacyRequired(true)),
    ];
    return () => {
      for (const unsubscribe of unsubscribers) {
        unsubscribe();
      }
    };
  }, [navigate]);

  return null;
}

function RootComponent() {
  return (
    <QueryClientProvider client={queryClient}>
      <FerminEventsBridge />
      <Outlet />
      {/* Hoja "Instala Fermin": solo se abre a pedido (Inicio, Perfil o ?instalar=<variante>). */}
      <InstallHost />
    </QueryClientProvider>
  );
}

/** `errorComponent` de la raíz (T056): `GenericErrorScreen` sin códigos, `request_id` ni traza. */
export function RootErrorComponent({ reset }: ErrorComponentProps) {
  const router = useRouter();

  return (
    <GenericErrorScreen
      onRetry={() => {
        void queryClient.invalidateQueries();
        void router.invalidate().finally(reset);
      }}
    />
  );
}

function NotFoundScreen() {
  return (
    <Screen
      title={t('common.notFound.title')}
      lead={t('common.notFound.description')}
      align="center"
      footer={
        <Link to="/" className={buttonClasses({ variant: 'secondary' })}>
          {t('common.notFound.action')}
        </Link>
      }
    />
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundScreen,
  errorComponent: RootErrorComponent,
});
